import { useMerchantContext } from "lib/contexts/MerchantProvider";
import Bowser from "bowser";
import { FloAnalyticsPayload, AnalyticsProps, PixelConfig } from "lib/types/analytics";
import { analyticsEvents, analyticsTypes, constants } from "lib/utils/constants";
import { EventPayloadType } from "lib/types/analytics";
import { putRequest } from "lib/core/apiClient";
import { useUserContext } from "lib/contexts/UserProvider";
import { inIframe, utmParamKeys } from "lib/utils/helpers";
import {
  sendPaymentInfoFbqEvent,
  sendCheckoutInitiateFbqEvent,
  sendPaymentCompletedFbqEvent,
  sendAddToCartFbqEvent,
} from "lib/third-party/facebook";
import { getCartForAnalytics } from "lib/utils/analytics";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useSearchParams } from "react-router-dom";
import { AuthSource } from "lib/types/user";
import { sendAnalyticsEvent as sendAnalytics } from "./analytics";
import { getUserOrderHistory } from "lib/utils/user";
import useAnonymousId from "./useAnonymousId";
import { getClevertapEventPayload, sendCleverTapEvent } from "lib/third-party/clevertap";
import { useUserConsentContext } from "lib/contexts/UserConsentProvider";
import { canSendToDownstream, getAcceptedConsentCodes } from "lib/utils/consent";

let thirdPartyAnalytics: any = {};
let channel: string = "";
let clientMetadata: any = {};
let longSessionId: string = "";
// Merchant-supplied attributes, pushed in from the store script. Module scope
// like the values above: it comes from outside React and every event needs it,
// so holding it in state would only add renders.
let customAttributes: Record<string, string> = {};

const useSendAnalyticsEvent = () => {
  const {
    state: {
      merchant,
      consentState: { isConsentEnabled },
    },
  } = useMerchantContext();
  const {
    state: { answers, userConsents },
  } = useUserConsentContext();
  const { state } = useCheckoutContext();
  const {
    checkoutId,
    shopifySessionId,
    checkoutToken,
    checkoutView,
    billing,
    checkoutItems,
    segmentId,
    noteAttributes,
    giftCards,
  } = state;
  const {
    state: { userLoginType, user },
  } = useUserContext();
  const { anonymousId } = useAnonymousId();
  const [searchParams] = useSearchParams();

  const isCartPage = searchParams.get("page") === "cart" || searchParams.get("page") === "CART";

  const userAgentInfo = Bowser.parse(window?.navigator?.userAgent);

  // DPDPA: whether a downstream may get this event (merchant analytics global.consents).
  const canSend = (downstream: string) =>
    canSendToDownstream({
      isConsentEnabled,
      analyticsConfig: merchant?.analytics,
      downstream,
      acceptedConsents: getAcceptedConsentCodes(userConsents, answers),
    });

  const setThirdPartyAnalyticsData = (data: any) => {
    thirdPartyAnalytics = data;
  };

  const setCheckoutChannel = (data: string) => {
    channel = data;
  };

  const setClientMetadata = (data: any) => {
    clientMetadata = {
      brandUrl: data?.brand_url ?? "",
      clientIp: data?.client_ip ?? "",
      userAgent: data?.user_agent ?? "",
    };
  };

  const setLongSessionId = (data: string) => {
    longSessionId = data;
  };

  const setCustomAttributes = (data: Record<string, string>) => {
    customAttributes = data ?? {};
  };

  const getLongSessionId = () => {
    return longSessionId;
  };

  // Gift card split of the order, for the two events that close the funnel. All zero / false when no
  // gift card is applied.
  const getGiftCardOrderData = (metaBilling?: typeof billing) => {
    const gcCount = giftCards?.length ?? 0;
    const gcAmount = (giftCards ?? []).reduce((sum, card) => sum + (card?.amount ?? 0), 0);
    const remainingPayable = (metaBilling ?? billing)?.total_payable ?? 0;
    return {
      gc_count: gcCount,
      gc_amount: gcAmount,
      remaining_payable: remainingPayable,
      is_mixed_tender: gcCount > 0 && remainingPayable > 0,
    };
  };

  const sendAnalyticsEvent = ({
    eventName,
    eventFor = [analyticsTypes.SF_ANALYTICS],
    eventType,
    metaData,
    context = "checkout",
  }: AnalyticsProps) => {
    if (eventFor.indexOf(analyticsTypes.SF_ANALYTICS) > -1) {
      const finalSegmentId = metaData?.segmentId ?? segmentId;
      const bureauUserId = localStorage.getItem(constants.FLO_BUREAU_USER_ID);
      const utmParams = noteAttributes?.filter((param: any) => utmParamKeys.includes(param?.name));
      const eventPayload: EventPayloadType = {
        checkoutId: metaData?.checkoutId ?? checkoutId,
        tokenId: metaData?.checkoutToken ?? checkoutToken,
        uiData: {
          pageName: isCartPage ? "CART" : checkoutView,
          longSessionId: metaData?.longSessionId ? metaData.longSessionId : longSessionId,
          utmParams: utmParams,
          ...(metaData?.methodType && { paymentMethod: metaData?.methodType?.toString()?.toLowerCase() }),
          ...(metaData?.modeType && { paymentMode: metaData?.modeType?.toString()?.toLowerCase() }),
          ...(metaData?.couponData && { coupon_data: metaData?.couponData }),
          ...(metaData?.shippingData && { shippingData: metaData?.shippingData }),
          ...(metaData?.paymentData && { paymentData: metaData?.paymentData }),
          ...(metaData?.bannerData && { bannerData: metaData?.bannerData }),
          ...(metaData?.metadata && { metadata: metaData?.metadata }),
          ...(metaData?.outOfStockItems && { outOfStockItems: metaData?.outOfStockItems }),
          ...(metaData?.action && { action: metaData?.action }),
          ...(metaData?.authSource && { authSource: metaData?.authSource }),
          ...(metaData?.badgesData && { badgesData: metaData?.badgesData }),
          ...(metaData?.storeDetails && { ...metaData?.storeDetails }),
          ...(metaData?.cartData && { cartData: metaData?.cartData }),
          ...(metaData?.otpData && { otpData: metaData?.otpData }),
          ...(metaData?.paymentRetryData && { paymentRetryData: metaData?.paymentRetryData }),
          ...(metaData?.methodName && { methodName: metaData?.methodName }),
          ...(metaData?.upsellData && { upsellData: metaData?.upsellData }),
          ...(metaData?.addressData && { addressData: metaData?.addressData }),
          ...(metaData?.issueReason && { issueReason: metaData?.issueReason }),
          ...(metaData?.milestone && { milestone: metaData?.milestone }),
          ...(metaData?.milestones && { milestones: metaData?.milestones }),
          ...(metaData?.progressBarCategory && { progressBarCategory: metaData?.progressBarCategory }),
          ...(metaData?.progressBarType && { progressBarType: metaData?.progressBarType }),
          ...(metaData?.wishlistData && { wishlistData: metaData?.wishlistData }),
          ...(metaData?.itemSelectorData && { itemSelectorData: metaData?.itemSelectorData }),
          ...(metaData?.discountAchieverData && { discountAchieverData: metaData?.discountAchieverData }),
          ...(metaData?.addOnData && { addOnData: metaData?.addOnData }),
          ...(metaData?.itemDetails && { item_details: metaData?.itemDetails }),
          ...(metaData?.automationData && { automationData: metaData?.automationData }),
          ...(metaData?.errorResponses && { errorResponses: metaData?.errorResponses }),
          ...(metaData?.errorSource && { errorSource: metaData?.errorSource }),
          ...(finalSegmentId && { segmentId: finalSegmentId }),
          ...(metaData?.loadDuration && { loadDuration: metaData?.loadDuration }),
          ...(metaData?.turnstileData && { turnstileData: metaData?.turnstileData }),
          ...(Object.keys(customAttributes).length > 0 && { customAttributes }),
          ...(metaData?.phoneNumber && { phone: metaData?.phoneNumber }),
          ...([analyticsEvents.FLO_PAYMENT_METHOD_SELECTED, analyticsEvents.FLO_PAYMENT_COMPLETED].includes(
            eventName,
          ) && getGiftCardOrderData(metaData?.billing)),
          ...(metaData?.giftCardData && { gift_card_data: metaData?.giftCardData }),
        },
        clientData: {
          browserName: userAgentInfo?.browser?.name,
          browserVersion: userAgentInfo?.browser?.version,
          osName: userAgentInfo?.os?.name,
          osVersion: userAgentInfo?.os?.version,
          osVersionName: userAgentInfo?.os?.versionName,
          platformModel: userAgentInfo?.platform?.model,
          platformType: userAgentInfo?.platform?.type,
          engineName: userAgentInfo?.engine?.name,
          engineVersion: userAgentInfo?.engine?.version,
          isIFrame: inIframe(),
          analytics: thirdPartyAnalytics,
          brandUrl: merchant?.brandUrl ?? metaData?.brandUrl,
          ...clientMetadata,
        },
        userData: metaData?.userData
          ? {
              authSource: searchParams.get("auth") ? AuthSource.CUSTOM : AuthSource.SHOPFLO,
              customerType: getUserOrderHistory(merchant?.merchantId ?? ""),
              ...metaData?.userData,
              userId: metaData?.userData?.userId ?? bureauUserId,
              anonymousId: anonymousId,
            }
          : {
              type: userLoginType,
              userId: user?.uid ?? bureauUserId,
              authSource: searchParams.get("auth") ? AuthSource.CUSTOM : AuthSource.SHOPFLO,
              customerType: getUserOrderHistory(merchant?.merchantId ?? ""),
              anonymousId: anonymousId,
            },
        cart: getCartForAnalytics(metaData?.checkoutItems ?? checkoutItems, metaData?.billing ?? billing),
      };

      const payload: FloAnalyticsPayload = {
        eventName: eventName,
        merchantId: merchant?.merchantId ?? metaData?.merchantId,
        sessionId: metaData?.shopifySessionId ?? shopifySessionId,
        timestamp: Date.now(),
        eventPayload: eventPayload,
        eventType: eventType,
        channel: metaData?.channel ?? channel,
      };
      try {
        sendAnalytics(payload);
        // postAnalytics(payload);
      } catch (e) {
        console.error(e);
      }
      if (
        [
          analyticsEvents.FLO_AUTH_LOGIN_COMPLETED,
          analyticsEvents.FLO_PAYMENT_LOADED,
          analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
        ].includes(eventName)
      ) {
        try {
          const payload = {
            state: eventName,
            merchant_id: merchant?.merchantId ?? metaData?.merchantId,
          };
          putRequest(`/checkout/${metaData?.checkoutId ?? checkoutId}/state`, payload, "KRATOS_PRIVATE");
        } catch (error) {
          console.error(error);
        }
      }
    }

    const isClevertapEnabled = merchant?.analytics?.clevertap?.enabled;
    const clevertapAccountId = merchant?.analytics?.clevertap?.account_id;
    const cleverTapRegion = merchant?.analytics?.clevertap?.region;
    if (
      canSend("clevertap") &&
      Boolean(isClevertapEnabled) &&
      Boolean(clevertapAccountId) &&
      context === "checkout"
    ) {
      const clevertapMetaData = { ...metaData, checkoutId };
      const eventPayload = getClevertapEventPayload(
        checkoutItems,
        metaData?.billing ?? billing,
        channel,
        clevertapMetaData,
        eventName,
      );
      sendCleverTapEvent(
        { accountId: clevertapAccountId, region: cleverTapRegion ?? "IN" },
        eventName,
        eventPayload,
        user,
      );
    }
    if (Boolean(eventFor.includes(analyticsTypes.FACEBOOK_PIXEL))) {
      const isFbqEnabled = metaData?.isFbqEnabled ?? merchant?.analytics?.facebook?.enabled;
      const fbqPixelId = metaData?.fbqPixelId ?? merchant?.analytics?.facebook?.pixel_id;
      const metaMarketing = merchant?.analytics?.meta_marketing;

      const pixels: PixelConfig[] = [];
      const pixelArr = Array.isArray(fbqPixelId) ? fbqPixelId : [];
      if (canSend("facebook") && Boolean(isFbqEnabled) && pixelArr.length) {
        pixelArr
          .filter((p: any) => p?.enabled)
          .forEach((p: any) => pixels.push({ id: p.id, channels: p.channels }));
      }
      if (canSend("meta_marketing") && metaMarketing?.enabled && metaMarketing?.pixels?.length) {
        metaMarketing.pixels.forEach((p: any) => {
          if (p?.enabled && p?.pixel_id && p?.browser_side) {
            pixels.push({ id: p.pixel_id, channels: p.channels });
          }
        });
      }
      if (!pixels.length) return;

      const userDetails = {
        userName: metaData?.userData?.name,
        userEmail: metaData?.userData?.email,
        userPhone: metaData?.userData?.phone,
      };
      if (
        (eventName === analyticsEvents.FLO_COOKIE_LOGIN ||
          eventName === analyticsEvents.FLO_AUTH_PHONE_LOADED) &&
        context === "checkout"
      ) {
        sendCheckoutInitiateFbqEvent({
          orderTotal: metaData?.totalPayable ?? billing.total_payable ?? 0,
          lineItems: metaData?.checkoutItems ?? checkoutItems,
          currency: "INR",
          sessionId: metaData?.shopifySessionId ?? shopifySessionId,
          pixelId: pixels,
          checkoutChannel: channel,
          ...{ userDetails },
        });
      }

      if (eventName === analyticsEvents.FLO_PAYMENT_METHOD_SELECTED) {
        sendPaymentInfoFbqEvent({
          orderTotal: metaData?.billing?.total_payable ?? billing.total_payable ?? 0,
          lineItems: checkoutItems,
          currency: "INR",
          sessionId: metaData?.shopifySessionId ?? shopifySessionId,
          pixelId: pixels,
          checkoutChannel: channel,
          ...{ userDetails },
        });
      }

      if (eventName === analyticsEvents.FLO_PAYMENT_COMPLETED) {
        sendPaymentCompletedFbqEvent({
          orderTotal: metaData?.orderAmount ?? metaData?.billing?.total_payable ?? billing.total_payable ?? 0,
          lineItems: checkoutItems,
          currency: "INR",
          sessionId: metaData?.shopifySessionId ?? shopifySessionId,
          pixelId: pixels,
          checkoutChannel: channel,
          ...{ userDetails },
        });
      }

      if (eventName === analyticsEvents.FLO_ADDED_TO_CART_UI) {
        sendAddToCartFbqEvent({
          orderTotal: metaData?.billing?.total_payable ?? billing.total_payable ?? 0,
          lineItems: checkoutItems,
          currency: "INR",
          sessionId: metaData?.shopifySessionId ?? shopifySessionId,
          pixelId: pixels,
          checkoutChannel: channel,
          ...{ userDetails },
        });
      }
    }
  };
  return {
    sendAnalyticsEvent,
    setThirdPartyAnalyticsData,
    setCheckoutChannel,
    setClientMetadata,
    setLongSessionId,
    getLongSessionId,
    setCustomAttributes,
  };
};
export default useSendAnalyticsEvent;
